import { CssVarsThemeOptions, extendTheme } from '@mui/joy';

declare module '@mui/joy/styles' {
  interface Palette {
    brown: string;
    salmon: string;
    purple: string;
    purple1: string;
    purple2: string;
    purple3: string;
    purple4: string;
    grey1: string;
    grey2: string;
    grey3: string;
    beige: string;
    hoverBrown: string;
    greenColor: string;
    black: string;
  }

  interface TypographySystemOverrides {
    'h2-sans-serif': true;
    label: true;
    'body-xs': false;
  }

  interface FontFamilyOverrides {
    serif: true;
  }

  interface FontSizeOverrides {
    xs: false;
    xl3: false;
    xl4: false;
  }

  interface BreakpointOverrides {
    xs: false;
    sm: false;
    md: false;
    lg: false;
    xl: false;
    tooSmall: true;
    mobile: true;
    tablet: true;
    intermediateNavbar: true;
    intermediateHome: true;
    desktop: true;
    bigDesktop: true;
  }
}

const joyTheme = extendTheme({
  breakpoints: {
    values: {
      tooSmall: 0,
      mobile: 320,
      tablet: 600,
      intermediateNavbar: 854,
      intermediateHome: 1024,
      desktop: 1080,
      bigDesktop: 1600,
    },
  },
  fontFamily: {
    body: 'Basel',
    display: 'Basel',
    serif: 'Source',
  },
  fontWeight: {
    sm: 400,
    md: 500,
    lg: 600,
  },
  fontSize: {
    sm: '15px',
    md: '17px',
    lg: '18px',
    xl: '20px',
    xl2: '28px',
  },
  lineHeight: {
    md: '1.1',
    lg: '1.2',
    xl: '1.25',
  },
  typography: {
    h1: {
      fontFamily: 'var(--joy-fontFamily-serif)',
      fontWeight: 'var(--joy-fontWeight-lg)',
      fontOpticalSizing: 'auto',
      fontSize: 'var(--joy-fontSize-xl2)',
      lineHeight: 'var(--joy-lineHeight-xl)',
      color: 'var(--joy-palette-text-primary)',
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
    },
    h2: {
      fontFamily: 'var(--joy-fontFamily-serif)',
      fontWeight: 'var(--joy-fontWeight-lg)',
      fontOpticalSizing: 'auto',
      fontSize: 'var(--joy-fontSize-xl)',
      lineHeight: 'var(--joy-lineHeight-xl)',
      color: 'var(--joy-palette-text-primary)',
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
    },
    'h2-sans-serif': {
      fontFamily: 'var(--joy-fontFamily-display)',
      fontWeight: 'var(--joy-fontWeight-md)',
      fontSize: 'var(--joy-fontSize-xl)',
      lineHeight: 'var(--joy-lineHeight-lg)',
      color: 'var(--joy-palette-text-secondary)',
    },
    h3: {
      fontFamily: 'var(--joy-fontFamily-serif)',
      fontWeight: 'var(--joy-fontWeight-lg)',
      fontOpticalSizing: 'auto',
      fontSize: 'var(--joy-fontSize-lg)',
      lineHeight: 'var(--joy-lineHeight-md)',
      color: 'var(--joy-palette-text-primary)',
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
    },
    'body-lg': {
      fontFamily: 'var(--joy-fontFamily-display)',
      fontWeight: 'var(--joy-fontWeight-sm)',
      fontSize: 'var(--joy-fontSize-xl)',
      lineHeight: 'var(--joy-lineHeight-lg)',
      color: 'var(--joy-palette-text-secondary)',
    },
    'body-md': {
      fontFamily: 'var(--joy-fontFamily-display)',
      fontWeight: 'var(--joy-fontWeight-sm)',
      fontSize: 'var(--joy-fontSize-md)',
      lineHeight: 'var(--joy-lineHeight-lg)',
      color: 'var(--joy-palette-text-secondary)',
    },
    'body-sm': {
      fontFamily: 'var(--joy-fontFamily-display)',
      fontWeight: 'var(--joy-fontWeight-sm)',
      fontSize: 'var(--joy-fontSize-sm)',
      lineHeight: 'var(--joy-lineHeight-lg)',
      color: 'var(--joy-palette-text-secondary)',
    },
    label: {
      fontFamily: 'var(--joy-fontFamily-display)',
      fontWeight: 'var(--joy-fontWeight-md)',
      fontSize: 'var(--joy-fontSize-md)',
      lineHeight: 'var(--joy-lineHeight-lg)',
      color: 'var(--joy-palette-text-secondary)',
    },
    labelError: {
      fontFamily: 'var(--joy-fontFamily-display)',
      fontWeight: 'var(--joy-fontWeight-SM)',
      fontSize: 'var(--joy-fontSize-md)',
      lineHeight: 'var(--joy-lineHeight-lg)',
      color: 'var(--joy-palette-hoverBrown)',
    },
    link: {
      fontFamily: 'var(--joy-fontFamily-display)',
      fontWeight: 'var(--joy-fontWeight-sm)',
      fontSize: 'var(--joy-fontSize-md)',
      lineHeight: 'var(--joy-lineHeight-lg)',
      color: 'var(--joy-palette-grey3)',
      textDecoration: 'underline',
      cursor: 'pointer',
    },
  },
  colorSchemes: {
    light: {
      palette: {
        primary: {
          500: '#6064A7',
        },
        // Cann-L colors
        brown: '#AB3B1A',
        salmon: '#EC866A',
        purple: '#6064A7',
        purple1: '#CECFE4',
        purple2: '#7C7FB6',
        purple3: '#3E438E',
        purple4: '#171935',
        text: {
          icon: '#363131',
          primary: '#AB3B1A',
          secondary: '#363131',
          tertiary: '#363131',
        },
        grey1: '#605757',
        grey2: '#E1D9D6',
        grey3: '#867979',
        beige: '#F8F2F1',
        hoverBrown: '#832D14',
        greenColor: '#8CA884',

        // Frontend boiler colors
        grey: {
          100: '#F6F7F8',
          200: '#E2E5E9',
          300: '#B8BDC7',
          400: '#6E7A8F',
          500: '#212631',
        },
        blue: {
          200: '#ECEFFE',
          400: '#5051F9',
          500: '#4242D7',
        },
        green: { 500: '#40B58A' },
        red: { 500: '#FF5D5D' },
        yellow: { 500: '#FFC107' },
        white: '#FFFFFF',
        black: '#000000',
      },
    },
  },
} satisfies CssVarsThemeOptions);

export default joyTheme;

// Frontend boiler colors
declare module '@mui/joy/styles' {
  interface Palette {
    grey: {
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
    };
    white: string;
    blue: {
      200: string;
      400: string;
      500: string;
    };
    green: {
      500: string;
    };
    red: {
      500: string;
    };
    yellow: {
      500: string;
    };
  }
}

declare module '@mui/joy/styles' {
  interface TypographySystemOverrides {
    labelError: true;
    link: true;
  }
}
